import {
  Case,
  CaseProfile,
  CaseProfileAnswers,
  CaseProfileFieldKey,
} from '../types';
import { t } from './translations';

export interface CaseProfileFieldSpec {
  key: CaseProfileFieldKey;
  /** Asked as a question, because that is how it gets answered. */
  label: string;
  /** One line on what to look for; this is where the guidance lives. */
  helper: string;
  required: boolean;
  rows: number;
}

/**
 * The single definition of the guided description. CaseForm renders it with
 * MUI, CaseProfileDialog with Tailwind — sharing the questions rather than the
 * markup keeps each screen native to its host.
 */
export const CASE_PROFILE_FIELDS: CaseProfileFieldSpec[] = [
  {
    key: 'whyCame',
    label: t.caseProfile.fields.whyCame.label,
    helper: t.caseProfile.fields.whyCame.helper,
    required: true,
    rows: 3,
  },
  {
    key: 'currentSituation',
    label: t.caseProfile.fields.currentSituation.label,
    helper: t.caseProfile.fields.currentSituation.helper,
    required: false,
    rows: 3,
  },
  {
    key: 'background',
    label: t.caseProfile.fields.background.label,
    helper: t.caseProfile.fields.background.helper,
    required: false,
    rows: 3,
  },
  {
    key: 'hopes',
    label: t.caseProfile.fields.hopes.label,
    helper: t.caseProfile.fields.hopes.helper,
    required: false,
    rows: 3,
  },
];

export function emptyCaseProfileAnswers(): CaseProfileAnswers {
  return {
    whyCame: '',
    currentSituation: '',
    background: '',
    hopes: '',
  };
}

export function parseCaseProfile(data: any): CaseProfile | null {
  if (!data || typeof data !== 'object') return null;
  const str = (value: unknown) => (typeof value === 'string' ? value : '');
  return {
    whyCame: str(data.whyCame),
    currentSituation: str(data.currentSituation),
    background: str(data.background),
    hopes: str(data.hopes),
    updatedAt: data.updatedAt?.toDate?.() ?? new Date(0),
    updatedBy: str(data.updatedBy),
    updatedByName: str(data.updatedByName),
  };
}

export function profileAnswersFrom(profile: CaseProfile | null | undefined): CaseProfileAnswers {
  if (!profile) return emptyCaseProfileAnswers();
  const { updatedAt, updatedBy, updatedByName, ...answers } = profile;
  return answers;
}

/**
 * Legacy cases only carry a free-text description. Seeding `whyCame` with it
 * keeps the text where a reader would look for it, with no bulk migration.
 */
export function profileAnswersForCase(caseItem: Case | null | undefined): CaseProfileAnswers {
  if (!caseItem) return emptyCaseProfileAnswers();
  if (caseItem.profile) return profileAnswersFrom(caseItem.profile);
  return { ...emptyCaseProfileAnswers(), whyCame: caseItem.description || '' };
}

/**
 * `Case.description` stays the searchable, card-previewable plain text. Keeping
 * it derived means search, the 150-char preview and the timeline need no change.
 */
export function buildDescriptionFromProfile(answers: CaseProfileAnswers): string {
  return CASE_PROFILE_FIELDS.map(({ key, label }) => {
    const value = answers[key].trim();
    return value ? `${label}\n${value}` : '';
  })
    .filter(Boolean)
    .join('\n\n');
}

export function countAnsweredProfileFields(answers: CaseProfileAnswers): number {
  return CASE_PROFILE_FIELDS.filter(({ key }) => answers[key].trim() !== '').length;
}

export function trimProfileAnswers(answers: CaseProfileAnswers): CaseProfileAnswers {
  const trimmed = emptyCaseProfileAnswers();
  CASE_PROFILE_FIELDS.forEach(({ key }) => {
    trimmed[key] = answers[key].trim();
  });
  return trimmed;
}
